import {i18n} from '@online-offer/i18n';
import {
  Box,
  getResponsivePropValue,
  Heading,
  Image,
  Show,
  Stack,
  useDevice,
  VStack,
} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {always, any, values} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {Section} from '../../components/Section/Section';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {getDataForTechnicalParameters} from '../../utils/getDataForTechnicalParameters';
import {placeholderFallback} from '../../utils/placeholderFallback';
import {TechnicalParametersItem} from '../TechnicalParametersItem/TechnicalParametersItem';

// eslint-disable-next-line eag/no-css-property
const StyledArrow = styled.div<{length: string}>`
  width: 2px;
  height: ${({length}) => length}px;
  background-color: ${({theme}) => theme.colors.text.tertiary};
  position: absolute;

  &:before,
  &:after {
    content: '';
    position: absolute;
    left: 0;
    border-style: solid;
    border-color: ${({theme}) => theme.colors.text.tertiary} transparent;
  }
  &:after {
    top: 0;
    transform: translate(-40%, -50%);
    border-width: 0 4px 7px 4px;
  }
  &:before {
    bottom: 0;
    transform: translate(-40%, 50%);
    border-width: 7px 4px 0 4px;
  }
`;

// eslint-disable-next-line eag/no-css-property
const LeftStyledArrow = styled(StyledArrow)<{length: string; top: string; left: string}>`
  top: ${({top}) => top}px;
  left: ${({left}) => left}px;
`;

// eslint-disable-next-line eag/no-css-property
const BottomStyledArrow = styled(StyledArrow)<{length: string; bottom: string; left: string}>`
  transform: rotate(121deg);
  bottom: ${({bottom}) => bottom}px;
  left: ${({left}) => left}px;
`;

// eslint-disable-next-line eag/no-css-property
const RightStyledArrow = styled(StyledArrow)<{
  length: string;
  bottom: string;
  right?: string;
  left?: string;
}>`
  transform: rotate(66deg);
  bottom: ${({bottom}) => bottom}px;
  ${({right}) => isNotNilOrEmpty(right) && `right: ${right}px;`}
  ${({left}) => isNotNilOrEmpty(left) && `left: ${left}px;`}
`;

export const TechnicalParameters = () => {
  const {vehicle} = useGetOnlineOfferData();
  const data = getDataForTechnicalParameters(vehicle);
  const device = useDevice();
  // left box
  const heightArrowLeftPosition = match(device)
    .with('mobile', always(0 as const))
    .with('tablet', always(1 as const))
    .with('notebook', always(1 as const))
    .otherwise(always(1 as const));
  const heightArrowTopPosition = match(device)
    .with('mobile', always(1 as const))
    .with('tablet', always(2 as const))
    .with('notebook', always(2 as const))
    .otherwise(always(6 as const));
  // center box
  const lengthArrowLeftPosition = match(device)
    .with('mobile', always(3 as const))
    .with('tablet', always(3 as const))
    .with('notebook', always(3 as const))
    .otherwise(always(6 as const));
  const lengthArrowBottomPosition = match(device)
    .with('mobile', always(5 as const))
    .with('tablet', always(2 as const))
    .with('notebook', always(2 as const))
    .otherwise(always(4 as const));
  // right box
  const widthArrowLeftPosition = match(device)
    .with('mobile', always(16 as const))
    .with('tablet', always(19 as const))
    .with('notebook', always(20 as const))
    .otherwise(always(29.5 as const));
  const widthArrowBottomPosition = match(device)
    .with('mobile', always(3.7 as const))
    .with('tablet', always(0 as const))
    .with('notebook', always(0 as const))
    .otherwise(always(0.5 as const));

  if (
    !any(
      isNotNilOrEmpty,
      values([
        data.dimensions.height,
        data.dimensions.length,
        data.dimensions.width,
        data.details.trunkSpace,
        data.details.operatingWeight,
        data.details.weight,
        data.details.maximalLoadCapacity,
      ])
    )
  ) {
    return null;
  }

  return (
    <Section id="TECHNICAL_PARAMETERS">
      <Stack justify="space-between" direction={['column', 'row']}>
        <Box paddingLeft={9} position="relative" paddingBottom={[16, 0]}>
          <HeightArrowContainer left={heightArrowLeftPosition} top={heightArrowTopPosition}>
            <Heading size={5} headingLevel={4} color="secondary" align="center">
              {`${placeholderFallback(data.dimensions.height)} mm`}
            </Heading>
          </HeightArrowContainer>
          <LengthArrowContainer left={lengthArrowLeftPosition} bottom={lengthArrowBottomPosition}>
            <Heading size={5} headingLevel={4} color="secondary" align="center">
              {`${placeholderFallback(data.dimensions.length)} mm`}
            </Heading>
          </LengthArrowContainer>
          <WidthArrowContainer left={widthArrowLeftPosition} bottom={widthArrowBottomPosition}>
            <Heading isSingleLine size={5} headingLevel={4} color="secondary" align="center">
              {`${placeholderFallback(data.dimensions.width)} mm`}
            </Heading>
          </WidthArrowContainer>
          <Box position="relative">
            <Image
              borderRadius="small"
              src="../../assets/images/vehicle.jpeg"
              height="100%"
              alt={i18n.t('technicalParameters.vehicleImage')}
              width={[65, 80, 80, 136]}
              isLazy
              hasSpinner
            />
            <LeftStyledArrow
              length={getResponsivePropValue(device, [82, 119, 119, 193])}
              top={getResponsivePropValue(device, [10, 10, 10, 39])}
              left={getResponsivePropValue(device, [-10, 0])}
            />
            <BottomStyledArrow
              length={getResponsivePropValue(device, [159, 190, 190, 303])}
              bottom={getResponsivePropValue(device, [-44, -44, -44, -71])}
              left={getResponsivePropValue(device, [70, 90, 90, 138])}
            />
            <RightStyledArrow
              length={getResponsivePropValue(device, [95, 109, 109, 176])}
              bottom={getResponsivePropValue(device, [-30, -30, -30, -50])}
              right={getResponsivePropValue(device, [undefined, 50, 50, 110])}
              left={getResponsivePropValue(device, [220, undefined])}
            />
          </Box>
        </Box>
        <Box paddingRight={[0, 0, 0, 16]}>
          <VStack spacing={[4, 6, 6, 8]}>
            <Show when={isNotNilOrEmpty(data.details.trunkSpace)}>
              <TechnicalParametersItem
                value={`${data.details.trunkSpace} l`}
                icon="technicalParams/trunk"
                title={i18n.t('technicalParameters.item.trunk')}
              />
            </Show>
            <Show when={isNotNilOrEmpty(data.details.operatingWeight)}>
              <TechnicalParametersItem
                value={`${data.details.operatingWeight} kg`}
                icon="technicalParams/weight"
                title={i18n.t('technicalParameters.item.towing')}
              />
            </Show>
            <Show when={isNotNilOrEmpty(data.details.weight)}>
              <TechnicalParametersItem
                value={`${data.details.weight} kg`}
                icon="technicalParams/weight"
                title={i18n.t('technicalParameters.item.weight')}
              />
            </Show>
            <Show when={isNotNilOrEmpty(data.details.maximalLoadCapacity)}>
              <TechnicalParametersItem
                value={`${data.details.maximalLoadCapacity} kg`}
                icon="technicalParams/weight"
                title={i18n.t('technicalParameters.item.maximalLoadCapacity')}
              />
            </Show>
          </VStack>
        </Box>
      </Stack>
    </Section>
  );
};

// eslint-disable-next-line eag/no-css-property
const HeightArrowContainer = styled.div<{left: number; top: number}>`
  position: absolute;
  z-index: ${({theme}) => theme.zIndices.CONTENT};
  left: ${({left}) => left}rem;
  top: ${({top}) => top}rem;
  writing-mode: vertical-lr;
  transform: rotate(-180deg);
`;

// eslint-disable-next-line eag/no-css-property
const LengthArrowContainer = styled.div<{left: number; bottom: number}>`
  position: absolute;
  z-index: ${({theme}) => theme.zIndices.CONTENT};
  left: ${({left}) => left}rem;
  bottom: ${({bottom}) => bottom}rem;
`;

// eslint-disable-next-line eag/no-css-property
const WidthArrowContainer = styled.div<{left: number; bottom: number}>`
  position: absolute;
  z-index: ${({theme}) => theme.zIndices.CONTENT};
  left: ${({left}) => left}rem;
  bottom: ${({bottom}) => bottom}rem;
`;
